import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { KineticText } from "@/components/KineticText";
import { IMAGES, PROFILE } from "@/data/profile";

const EASE = [0.76, 0, 0.24, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden text-white">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[10%] h-[120%]">
        <img src={IMAGES.hero} alt="Offshore semi-submersible drilling rig at sunset" className="img-treat h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1b2229] via-[#1b2229]/45 to-[#1b2229]/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1b2229]/70 via-[#1b2229]/20 to-transparent" />
      </motion.div>

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 px-6 md:px-12 pt-28 md:pt-32 pb-14 md:pb-20 min-h-screen flex flex-col">
        <div className="flex items-center justify-between eyebrow !text-white/80">
          <span className="flex items-center gap-3" data-testid="hero-status">
            <span className="live-dot inline-block w-2 h-2 bg-[var(--accent)]" />
            {PROFILE.availability}
          </span>
          <span className="hidden sm:block">{PROFILE.location}</span>
        </div>

        <div className="mt-10 md:mt-12">
          <motion.figure
            initial={{ opacity: 0, y: 40, clipPath: "inset(100% 0 0 0)" }}
            animate={{ opacity: 1, y: 0, clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, ease: EASE }}
            className="m-0 w-[min(100%,300px)] md:w-[min(30vw,300px)]"
            data-testid="hero-portrait"
          >
            <div className="relative border-2 border-white/90 bg-[var(--steel)] p-2 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]">
              <img src={IMAGES.portrait} alt="Michael J. Warden" className="block w-full aspect-square object-cover" />
              <span className="absolute -top-3 -left-3 w-6 h-6 bg-[var(--accent)]" />
            </div>
            <figcaption className="mt-4 flex flex-col gap-1 eyebrow !text-[var(--accent-2)]">
              <span>Founder — {PROFILE.company}</span>
              <span>Since 2000</span>
            </figcaption>
          </motion.figure>

          <div className="mt-8 md:mt-10">
            <KineticText
              testId="hero-title"
              delay={0.5}
              lines={["Michael J.", "Warden"]}
              className="font-display font-bold uppercase leading-[0.86] tracking-[-0.02em] text-[clamp(3.5rem,min(13vw,22vh),12rem)]"
            />
            <div className="grid grid-cols-12 gap-6 mt-6 md:mt-8 items-end">
              <div className="col-span-12 lg:col-span-6">
                <KineticText
                  as="p"
                  delay={0.85}
                  stagger={0.1}
                  lines={["Offshore Drilling Engineer.", `${PROFILE.years} years. 14 rigs. Zero excuses.`]}
                  className="font-display uppercase text-xl md:text-2xl text-[var(--accent-2)] leading-tight"
                />
              </div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.3, duration: 1 }}
                className="col-span-12 lg:col-span-5 lg:col-start-8 text-sm md:text-base text-white/80 max-w-md"
                data-testid="hero-tagline"
              >
                {PROFILE.tagline}
              </motion.p>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="hazard h-2 relative z-10" />
      <div className="relative z-10 bg-[var(--steel)] px-6 md:px-12 h-14 flex items-center justify-between eyebrow !text-white/70">
        <span>Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={14} />
        </motion.span>
        <span>Gulf of Mexico · N 27° 30' — W 90° 00'</span>
      </div>
    </section>
  );
};
